import React, { useEffect, useRef } from 'react';

import { cssMap } from '@compiled/react';
import { defineMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

const defaultErrorMessage = defineMessage({
	id: 'jsmCreateSurveyCardError.heading',
	defaultMessage:
		"Rovo couldn't create your survey. Refresh the page, or try again with a different prompt.",
	description: 'Default error message displayed when an unhandled error occurs.',
});

const refreshButton = defineMessage({
	id: 'jsmCreateSurveyCardError.refreshButton',
	defaultMessage: 'Refresh page',
	description: 'Button label to refresh the page when an error occurs.',
});

const styles = cssMap({
	container: {
		marginBlockStart: token('space.150'),
	},
	iconContainer: {
		marginBlockStart: token('space.025'),
	},
	buttonWrapper: {
		alignSelf: 'start',
	},
});

export const JsmCreateSurveyCardError = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const hasFiredViewEvent = useRef(false);

	useEffect(() => {
		if (!hasFiredViewEvent.current) {
			hasFiredViewEvent.current = true;
			sendAnalyticsEvent({
				action: 'rendered',
				actionSubject: 'component',
				actionSubjectId: 'JsmCreateSurveyCardError',
			});
		}
	}, [sendAnalyticsEvent]);

	const handleRefresh = () => window.location.reload();

	return (
		<Stack space="space.150">
			<Inline space="space.075" alignBlock="start" xcss={styles.container}>
				<Box xcss={styles.iconContainer}>
					<StatusErrorIcon color={token('color.icon.danger')} label="" />
				</Box>
				<Text>{formatMessage(defaultErrorMessage)}</Text>
			</Inline>
			<Box xcss={styles.buttonWrapper}>
				<Button onClick={handleRefresh}>{formatMessage(refreshButton)}</Button>
			</Box>
		</Stack>
	);
};
